import { Alert, HFlow, Icon, Text, Tooltip, useTheme, VFlow } from 'bold-ui'
import { ColorScale } from 'bold-ui/lib/styles/colors'
import { Ellipsis } from 'components/Ellipsis'
import { format } from 'date-fns'
import { css } from 'emotion'
import { AcompanhamentoIdosoCardQuery, RiscoVulnerabilidadeIvcfEnum, UltimoIvcfQuery } from 'graphql/types.generated'
import { Fragment } from 'react'
import { isUndefinedOrNull } from 'util/checks'
import { humanizeDate } from 'util/date/humanize-date'

import { IVCF_RESULTADOS, IVCF_SUBGRUPOS, IvcfFieldModel } from '../../objetivo/ivcf/model-ivcf'
import { ContentCardSection } from '../components/ContentCardSection'

interface AcompanhamentoIdosoCardProps {
  data: AcompanhamentoIdosoCardQuery
  loading: boolean
  canRender: boolean
  isFolhaDeRosto: boolean
  ivcfAtendimentoAtual?: IvcfFieldModel
  ultimoIvcf: UltimoIvcfQuery['ultimoIvcf']
}

const humanizedDate = (data: Instant) => {
  const humanized = humanizeDate(data)
  return humanized === 'Hoje' ? humanized : 'Há ' + humanized
}

const renderAvalMulti = (data: Instant) => (
  <VFlow vSpacing={0.1}>
    <Text fontWeight='bold'>Última avaliação multidimensional</Text>
    {isUndefinedOrNull(data) ? '-' : `${format(data, 'dd/MM/yyyy')} | ${humanizedDate(data)}`}
  </VFlow>
)

export function AcompanhamentoIdosoCard(props: AcompanhamentoIdosoCardProps) {
  const { data, loading, canRender, isFolhaDeRosto, ivcfAtendimentoAtual, ultimoIvcf } = props

  const theme = useTheme()

  const renderUltimoIvcf = (ultimoIvcf: UltimoIvcfQuery['ultimoIvcf']) => {
    return renderIvcf(
      ultimoIvcf?.ivcf?.riscoVulnerabilidade ?? ultimoIvcf?.fatoIvcf?.riscoVulnerabilidade,
      ultimoIvcf?.ivcf?.resultado ?? ultimoIvcf?.fatoIvcf?.resultado,
      ultimoIvcf?.ivcf?.dimensoesAlteradas ?? ultimoIvcf?.fatoIvcf?.dimensoesAlteradas,
      ultimoIvcf?.ivcf?.dataResultado ?? ultimoIvcf?.fatoIvcf?.dataResultado,
      ultimoIvcf?.ivcf?.atendimentoProfissional.lotacao.profissional.nome ??
        ultimoIvcf?.fatoIvcf?.dimensaoProfissional?.nome,
      ultimoIvcf?.ivcf?.atendimentoProfissional.lotacao.cbo.nome ?? ultimoIvcf?.fatoIvcf?.dimensaoCbo?.nome
    )
  }

  const renderIvcf = (
    riscoVulnerabilidade?: RiscoVulnerabilidadeIvcfEnum,
    resultado?: number,
    dimensoesAlteradas?: string,
    dataResultado?: Instant,
    nomeProfissional?: string,
    cboProfissional?: string
  ) => {
    const color = riscoVulnerabilidade ? IVCF_RESULTADOS[riscoVulnerabilidade].colorScale : theme.pallete.primary
    const classes = createStyles(color)
    const text =
      !riscoVulnerabilidade && resultado == null
        ? 'Não registrado'
        : `${IVCF_RESULTADOS[riscoVulnerabilidade].longTitle} | ${resultado} ${resultado === 1 ? 'ponto' : 'pontos'}`
    const registradoEm = dataResultado
      ? `Registrado em ${format(dataResultado, 'dd/MM/yyyy')} | ${humanizedDate(dataResultado)}`
      : null

    return (
      <VFlow vSpacing={0.5}>
        <Text>
          <Text fontWeight='bold'>Índice de Vulnerabilidade Clínico-Funcional </Text>
          <div css={{ display: 'inline-block' }}>
            <HFlow alignItems='center' hSpacing={0.5}>
              <Text>(IVCF-20)</Text>
              {!isFolhaDeRosto && ivcfAtendimentoAtual && (
                <Tooltip text='Registrado agora'>
                  <Icon icon='clockOutline' color={theme.pallete.primary.c40} size={1} />
                </Tooltip>
              )}
            </HFlow>
          </div>
          {dataResultado && !isFolhaDeRosto && !ivcfAtendimentoAtual && <HFlow>{registradoEm}</HFlow>}
        </Text>
        <Alert type='info' inline styles={{ wrapper: classes.wrapper, container: classes.container }}>
          {text}
        </Alert>
        {dimensoesAlteradas != null && (
          <VFlow vSpacing={0.1}>
            <Text fontWeight='bold'>Dimensões alteradas:</Text>
            <Text>{dimensoesAlteradas || 'Nenhuma alteração'}.</Text>
          </VFlow>
        )}
        {dataResultado && nomeProfissional && isFolhaDeRosto && (
          <Ellipsis maxLines={3}>
            {registradoEm} por {nomeProfissional.truncate(16)} | {cboProfissional.capitalize()}
          </Ellipsis>
        )}
      </VFlow>
    )
  }

  return (
    <ContentCardSection isLoading={loading} messageLoading='Carregando dados da pessoa idosa' canRender={canRender}>
      <VFlow>
        <Fragment>
          {!isFolhaDeRosto && ivcfAtendimentoAtual
            ? renderIvcf(
                ivcfAtendimentoAtual?.riscoVulnerabilidade,
                ivcfAtendimentoAtual?.resultado,
                ivcfAtendimentoAtual?.dimensoesAlteradas
                  .map((dimensaoAlterada) => IVCF_SUBGRUPOS[dimensaoAlterada].label)
                  .join('; ')
              )
            : renderUltimoIvcf(ultimoIvcf)}
        </Fragment>
        {renderAvalMulti(data.ultimaAvaliacaoMultidimensional)}
      </VFlow>
    </ContentCardSection>
  )
}

const createStyles = (color: ColorScale) => ({
  wrapper: css`
    background-color: ${color.c90};
    border: 1px solid ${color.c60};
    color: ${color.c40};
    display: flex;
  `,
  container: css`
    align-items: center;
  `,
})
